import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FeedbackItem {
  id: number;
  message: string;
  page: string | null;
  status: string;
  created_at: string;
}

// ─── Fetch helper ────────────────────────────────────────────────────────────

const API = '/api/portal';

async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'same-origin',
  });
  const data = await res.json() as T & { error?: string };
  if (!res.ok) throw new Error(data.error || 'error');
  return data;
}

export const feedbackKeys = {
  root: ['feedback'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useFeedbackList() {
  return useQuery({
    queryKey: feedbackKeys.root,
    queryFn: () => req<{ feedback: FeedbackItem[] }>('/feedback'),
  });
}

export function useSendFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { message: string; page?: string }) =>
      req<{ ok: boolean }>('/feedback', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: feedbackKeys.root }),
  });
}
